import { useState, useEffect, useCallback } from 'react';
import { 
  AdvertisingFilterState,
  filterStateManager,
  initializeFilterStateManager,
 } from '../../shared/filterStateManager';

export interface UseAdvertisingFiltersReturn {
  startDate: Date | null;
  endDate: Date | null;
  brand: string[];
  skus: string[];
  channel: string;
  keyword: string[];
  campaign: string[];
  
  setStartDate: (date: Date | null) => void;
  setEndDate: (date: Date | null) => void;
  setBrand: (brand: string[]) => void;
  setSkus: (skus: string[]) => void;
  setChannel: (channel: string) => void;
  setKeyword: (keyword: string[]) => void;
  setCampaign: (campaign: string[]) => void;
  
  clearFilters: () => void;
  hasActiveFilters: boolean;
  isLoading: boolean;
}


export const useAdvertisingFilters = (): UseAdvertisingFiltersReturn => {
  const [isLoading, setIsLoading] = useState(true);
  const [filterState, setFilterState] = useState<AdvertisingFilterState>({
    startDate: null,
    endDate: null,
    brand: [],
    skus: [],
    channel: '',
    keyword: [],
    campaign: [],
  });

  const loadFiltersFromSheet = useCallback(async () => {
    setIsLoading(true);
    try {
      await Excel.run(async (context) => {
        const activeSheet = context.workbook.worksheets.getActiveWorksheet();
        activeSheet.load("name");
        
        await context.sync();
        
        const currentSheetName = activeSheet.name;
        console.log(`📖 Reading filters from sheet: "${currentSheetName}"`);

        // Ensure filterStateManager is initialized
        if (!filterStateManager.isInitialized()) {
          await filterStateManager.initialize();
        }
        
        const loadedState = await filterStateManager.hydrateAdvertisingFiltersForSheet(currentSheetName);
        console.log("🔁 useAdvertisingFilters.loadFiltersFromSheet - loaded from custom props:", {
          sheet: currentSheetName,
          loadedState,
        });

        if (loadedState && (loadedState.startDate || loadedState.endDate || loadedState.brand.length > 0 || loadedState.skus.length > 0 || loadedState.keyword.length > 0 || loadedState.campaign.length > 0)) {
          setFilterState(loadedState);
          console.log(`✅ Filters loaded from state manager:`, loadedState);
        } else {
          console.log(`📭 No filters found for sheet "${currentSheetName}" in state manager`);
          
          if (currentSheetName.toLowerCase() === "advertising dashboard") {
            const end = new Date();
            end.setDate(end.getDate() - 1);
            const start = new Date();
            start.setDate(start.getDate() - 2);
            setFilterState({
              startDate: start,
              endDate: end,
              brand: [],
              skus: [],
              channel: '',
              keyword: [],
              campaign: [],
            });
          } else {
            setFilterState({
              startDate: null,
              endDate: null,
              brand: [],
              skus: [],
              channel: '',
              keyword: [],
              campaign: [],
            });
          }
        }
      });
    } catch (error) {
      console.error('❌ Error loading filters:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFiltersFromSheet();

    return () => {
      // No-op for now, as we rely on onActivated for refresh
    };
  }, [loadFiltersFromSheet]);

  useEffect(() => {
    let handler: any = null;
    
    Excel.run(async (context) => {
      handler = context.workbook.worksheets.onActivated.add(async () => {
        console.log('📋 Sheet changed - reloading filters');
        await loadFiltersFromSheet();
      });
      await context.sync();
    }).catch(err => console.warn("Failed to register sheet activation handler:", err));

    return () => {
      if (handler) {
        Excel.run(async (context) => {
          context.workbook.worksheets.onActivated.remove(handler);
          await context.sync();
        }).catch(() => {});
      }
    };
  }, [loadFiltersFromSheet]);

  const setStartDate = useCallback((date: Date | null) => {
    setFilterState(prev => ({ ...prev, startDate: date }));
  }, []);

  const setEndDate = useCallback((date: Date | null) => {
    setFilterState(prev => ({ ...prev, endDate: date }));
  }, []);

  const setBrand = useCallback((brand: string[]) => {
    setFilterState(prev => ({ ...prev, brand }));
  }, []);

  const setSkus = useCallback((skus: string[]) => {
    setFilterState(prev => ({ ...prev, skus }));
  }, []);

  const setChannel = useCallback((channel: string) => {
    setFilterState(prev => ({ ...prev, channel }));
  }, []);

  const setKeyword = useCallback((keyword: string[]) => {
    setFilterState(prev => ({ ...prev, keyword }));
  }, []);

  const setCampaign = useCallback((campaign: string[]) => {
    setFilterState(prev => ({ ...prev, campaign }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilterState({
      startDate: null,
      endDate: null,
      brand: [],
      skus: [],
      channel: '',
      keyword: [],
      campaign: [],
    });
  }, []);

  const hasActiveFilters = Boolean(
    filterState.startDate || 
    filterState.endDate || 
    filterState.brand.length > 0 || 
    filterState.skus.length > 0 || 
    filterState.channel ||
    filterState.keyword.length > 0 ||
    filterState.campaign.length > 0
  );

  return {
    startDate: filterState.startDate,
    endDate: filterState.endDate,
    brand: filterState.brand,
    skus: filterState.skus,
    channel: filterState.channel,
    keyword: filterState.keyword,
    campaign: filterState.campaign,
    setStartDate,
    setEndDate,
    setBrand,
    setSkus,
    setChannel,
    setKeyword,
    setCampaign,
    clearFilters,
    hasActiveFilters,
    isLoading,
  };
};
